import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { Button, Input, Select } from '@/components/ui';
import { CTA, GHOST } from '@components/ui/designTokens';
import { LocationPicker } from '@/components/maps/LocationPicker';
import {
  useProvinces,
  useDistricts,
  useTownships,
} from '@/features/customers/hooks/useGeolocation';
import type { Province, District, Township } from '@/features/customers/types/geolocation.types';
import { useGeolocationByCoordinates } from '../../hooks/useBranchOffice';
import type { NewCompanyStep2Data } from '../../types/newCompanyWizard.types';

interface Step2FiscalAddressProps {
  data: NewCompanyStep2Data;
  onNext: (data: NewCompanyStep2Data) => void;
  onBack: () => void;
}

export function Step2FiscalAddress({ data, onNext, onBack }: Step2FiscalAddressProps) {
  const [coordinates, setCoordinates] = useState(data.coordinates);
  const [geolocationData, setGeolocationData] = useState(data.geolocationData);
  const [provinceId, setProvinceId] = useState(data.provinceId);
  const [districtId, setDistrictId] = useState(data.districtId);
  const [townshipId, setTownshipId] = useState(data.townshipId);
  const [addressDetail, setAddressDetail] = useState(data.addressDetail);
  const [homeOrFloorNumber, setHomeOrFloorNumber] = useState(data.homeOrFloorNumber);
  // El código de ubicación fiscal (Aludra `/mdl18/electronicInvoiceGetUbi`) ya
  // NO se resuelve acá: ese servicio es lento/inestable (ver
  // docs/FISCAL_CODE_PENDIENTE.md) y no debe frenar el alta de una compañía.
  // Solo se resuelve al crear una sucursal real (BranchOfficeFormModal). Se
  // sigue mandando el campo (vacío) porque el payload de creación lo acepta
  // como opcional.
  const fiscalCode = data.fiscalCode;

  // Texto libre de los inputs de latitud/longitud — separado de `coordinates`
  // para permitir que el usuario escriba (ej. "9.", "-79") sin que cada tecla
  // dispare una búsqueda de geolocalización inválida.
  const [latInput, setLatInput] = useState(data.coordinates ? String(data.coordinates.lat) : '');
  const [lngInput, setLngInput] = useState(data.coordinates ? String(data.coordinates.lng) : '');
  const [coordsError, setCoordsError] = useState<string | null>(null);

  // Si las coordenadas cambian por otra vía (clic en el mapa, búsqueda de
  // Google), reflejar el valor en los inputs editables.
  useEffect(() => {
    setLatInput(coordinates ? String(coordinates.lat) : '');
    setLngInput(coordinates ? String(coordinates.lng) : '');
  }, [coordinates]);

  const { data: provinces, isLoading: isLoadingProvinces } = useProvinces();
  const { data: districts, isLoading: isLoadingDistricts } = useDistricts(provinceId);
  const { data: townships, isLoading: isLoadingTownships } = useTownships(districtId);

  const geolocationMutation = useGeolocationByCoordinates();

  const handleLocationSelect = async (latitude: number, longitude: number, address?: string) => {
    setCoordinates({ lat: latitude, lng: longitude });
    if (address) setAddressDetail(address);

    try {
      const geo = await geolocationMutation.mutateAsync({
        latitude: latitude.toString(),
        longitude: longitude.toString(),
      });
      setGeolocationData(geo);
      setProvinceId(geo.ProvinceId || '');
      setDistrictId(geo.DistrictId || '');
      setTownshipId(geo.TownshipId || '');
    } catch {
      await Swal.fire({
        title: 'Error',
        text: 'No se pudo obtener la información de ubicación para estas coordenadas.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
    }
  };

  // Confirma latitud/longitud tipeadas a mano: valida el rango y, si son
  // válidas, reutiliza el mismo flujo que un clic en el mapa (mueve el
  // marcador y resuelve provincia/distrito/corregimiento).
  const commitManualCoordinates = () => {
    const lat = parseFloat(latInput);
    const lng = parseFloat(lngInput);

    if (
      latInput.trim() === '' ||
      lngInput.trim() === '' ||
      Number.isNaN(lat) ||
      Number.isNaN(lng) ||
      lat < -90 ||
      lat > 90 ||
      lng < -180 ||
      lng > 180
    ) {
      setCoordsError('Latitud/longitud inválidas');
      return;
    }

    if (coordinates && coordinates.lat === lat && coordinates.lng === lng) {
      setCoordsError(null);
      return;
    }

    setCoordsError(null);
    handleLocationSelect(lat, lng);
  };

  const handleTownshipChange = (newTownshipId: string) => {
    setTownshipId(newTownshipId);
  };

  const isValid =
    coordinates &&
    provinceId &&
    districtId &&
    townshipId &&
    addressDetail.trim() &&
    homeOrFloorNumber.trim();

  const handleSubmit = () => {
    if (!isValid) {
      Swal.fire({
        title: 'Ubicación',
        text: 'Revise los datos suministrados, parece que hay campos requeridos sin completar.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    onNext({
      coordinates,
      geolocationData,
      provinceId,
      districtId,
      townshipId,
      addressDetail,
      homeOrFloorNumber,
      fiscalCode,
    });
  };

  return (
    <div className="font-display" data-testid="company-wizard-step2-container">
      {/*
        Grid en vez de flex con ancho fijo: las columnas quedan en 1fr/1fr
        (mismo ancho, mapa y formulario a la par) y `items-stretch` (default
        de grid) hace que ambas midan lo mismo de alto también — el alto de
        la fila lo define el contenido del formulario (auto), y el mapa —con
        `fillParent`— crece via flexbox para llenar exactamente esa altura,
        en vez de un px fijo que quedaba más alto/bajo y forzaba scroll.
      */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-stretch">
        {/* Mapa */}
        <div className="flex flex-col">
          <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-3">
            Haga clic en el mapa para ubicar la sede fiscal, o escriba la latitud y
            longitud manualmente.
          </p>
          <div className="h-[320px] lg:h-auto lg:flex-1 lg:min-h-[320px]">
            <LocationPicker
              onLocationSelect={handleLocationSelect}
              fillParent
              // Buscador de direcciones (Places Autocomplete) deshabilitado
              // temporalmente: el proyecto de Google Cloud de esta API key no
              // tiene habilitada "Places API" (legacy), lo que tira
              // "LegacyApiNotActivatedMapError" en consola. Pendiente:
              // docs/PLACES_API_PENDIENTE.md
              // showSearch
              position={coordinates}
            />
          </div>

          {geolocationMutation.isPending && (
            <p className="mt-2 text-sm text-secondary-500">Buscando ubicación...</p>
          )}
        </div>

        {/*
          Datos de la dirección, de a 2 campos por fila — 3 selects juntos
          (Provincia/Distrito/Corregimiento) quedaban muy angostos y el texto
          largo se superponía con la flechita del combo. Corregimiento va en
          su propia fila porque suele traer los nombres más largos.
          `gap-6` reparte las filas a lo largo del alto disponible, que
          gracias al `items-stretch` de arriba termina siendo el mismo que
          ocupa el mapa.
        */}
        <div className="flex flex-col gap-6 min-w-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Select
              label="Provincia"
              required
              value={provinceId}
              onChange={(e) => {
                setProvinceId(e.target.value);
                setDistrictId('');
                setTownshipId('');
              }}
              disabled={isLoadingProvinces}
              data-testid="company-wizard-step2-province-select"
            >
              <option value="">Seleccione una provincia</option>
              {provinces?.map((province: Province) => (
                <option key={province.ProvinceId} value={province.ProvinceId}>
                  {province.Name}
                </option>
              ))}
            </Select>

            <Select
              label="Distrito"
              required
              value={districtId}
              onChange={(e) => {
                setDistrictId(e.target.value);
                setTownshipId('');
              }}
              disabled={!provinceId || isLoadingDistricts}
              data-testid="company-wizard-step2-district-select"
            >
              <option value="">Seleccione un distrito</option>
              {districts?.map((district: District) => (
                <option key={district.DistrictId} value={district.DistrictId}>
                  {district.Name}
                </option>
              ))}
            </Select>
          </div>

          <Select
            label="Corregimiento"
            required
            value={townshipId}
            onChange={(e) => handleTownshipChange(e.target.value)}
            disabled={!districtId || isLoadingTownships}
            data-testid="company-wizard-step2-township-select"
          >
            <option value="">Seleccione un corregimiento</option>
            {townships?.map((township: Township) => (
              <option key={township.TownshipId} value={township.TownshipId}>
                {township.Name}
              </option>
            ))}
          </Select>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              label="Dirección"
              required
              value={addressDetail}
              onChange={(e) => setAddressDetail(e.target.value)}
              placeholder="Ej: Calle 50, Edificio Torre Global"
              data-testid="company-wizard-step2-address-detail-input"
            />
            <Input
              label="Casa / Oficina / Apartamento"
              required
              value={homeOrFloorNumber}
              onChange={(e) => setHomeOrFloorNumber(e.target.value)}
              placeholder="Ej: Piso 5, Oficina 501"
              data-testid="company-wizard-step2-home-or-floor-number-input"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              label="Latitud"
              required
              value={latInput}
              onChange={(e) => setLatInput(e.target.value)}
              onBlur={commitManualCoordinates}
              placeholder="Ej: 9.076500"
              inputMode="decimal"
              className="font-mono"
              error={coordsError || undefined}
              helperText={!coordsError ? 'Editable: también se actualiza al buscar o hacer clic en el mapa' : undefined}
              data-testid="company-wizard-step2-latitude-input"
            />
            <Input
              label="Longitud"
              required
              value={lngInput}
              onChange={(e) => setLngInput(e.target.value)}
              onBlur={commitManualCoordinates}
              placeholder="Ej: -79.530900"
              inputMode="decimal"
              className="font-mono"
              data-testid="company-wizard-step2-longitude-input"
            />
          </div>
        </div>
      </div>

      {/*
        Pie homologado con ProductForm (/products), sin `sticky`: acá el
        contenido puede ser más corto que el modal, y un pie sticky se
        quedaba pegado al fondo dejando un hueco vacío antes de llegar a él.
      */}
      <div className="-mx-9 -mb-7 mt-7 flex flex-wrap items-center justify-between gap-3 border-t border-[#f0f0f0] bg-[#fafafa] px-9 py-[18px] dark:border-secondary-700 dark:bg-secondary-900/95">
        <Button variant="outline" onClick={onBack} className={GHOST} data-testid="company-wizard-step2-previous-button">
          Anterior
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={geolocationMutation.isPending}
          className={CTA}
          data-testid="company-wizard-step2-next-button"
        >
          Siguiente
        </Button>
      </div>
    </div>
  );
}
